import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, PieChart, ArrowUpRight } from 'lucide-react';
interface InvestorSlide11Props {
  isDark?: boolean;
}
export function InvestorSlide11({
  isDark = false
}: InvestorSlide11Props) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-171">
      <div className="min-h-full flex flex-col justify-center max-w-6xl mx-auto px-6 sm:px-12 py-20 sm:py-24" data-id="element-172">
        <motion.div variants={itemVariants} className="mb-12 sm:mb-16 text-center" data-id="element-173">
          <h1 className={`text-3xl sm:text-5xl font-bold mb-6 sm:mb-8 tracking-tight ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-174">
            Financial Projections
          </h1>
          <p className={`text-lg sm:text-2xl max-w-3xl mx-auto ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-175">
            Path to $10M ARR in 24 months
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 mb-12 sm:mb-16" data-id="element-176">
          {/* Revenue Chart Placeholder */}
          <motion.div variants={itemVariants} className={`rounded-3xl p-8 sm:p-10 flex flex-col justify-between h-64 sm:h-80 ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige shadow-sm'}`} data-id="element-177">
            <div className="flex justify-between items-start" data-id="element-178">
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-179">
                Revenue Growth
              </h3>
              <BarChart3 className={`w-6 h-6 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-180" />
            </div>

            {/* Simple CSS Chart Representation */}
            <div className="flex items-end justify-between h-40 gap-4 mt-4" data-id="element-181">
              <div className="w-full bg-heliona-mint/20 rounded-t-lg h-[10%] relative group" data-id="element-182">
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-183">
                  $0.5M
                </div>
              </div>
              <div className="w-full bg-heliona-mint/40 rounded-t-lg h-[25%] relative group" data-id="element-184">
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-185">
                  $2.5M
                </div>
              </div>
              <div className="w-full bg-heliona-mint/60 rounded-t-lg h-[50%] relative group" data-id="element-186">
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-187">
                  $5.0M
                </div>
              </div>
              <div className="w-full bg-heliona-mint rounded-t-lg h-[100%] relative group" data-id="element-188">
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-189">
                  $10.0M
                </div>
              </div>
            </div>
            <div className="flex justify-between text-xs mt-2 opacity-60" data-id="element-190">
              <span data-id="element-191">Q2 '26</span>
              <span data-id="element-192">Q4 '26</span>
              <span data-id="element-193">Q2 '27</span>
              <span data-id="element-194">Q4 '27</span>
            </div>
          </motion.div>

          {/* Key Metrics */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 gap-4 sm:gap-6" data-id="element-195">
            {[{
            label: 'Revenue / Leader',
            value: '$2,400',
            sub: 'Annual Average'
          }, {
            label: 'LTV / CAC',
            value: '5:1',
            sub: 'Target Ratio'
          }, {
            label: 'Gross Margin',
            value: '75%+',
            sub: 'Asset-light model'
          }, {
            label: 'Expansion',
            value: '130%',
            sub: 'Net Revenue Retention'
          }].map((metric, idx) => <div key={idx} className={`rounded-2xl p-5 sm:p-6 flex flex-col justify-center ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige shadow-sm'}`} data-id="element-196">
                <div className={`text-2xl sm:text-3xl font-bold mb-1 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-197">
                  {metric.value}
                </div>
                <div className={`text-sm font-bold mb-1 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-198">
                  {metric.label}
                </div>
                <div className={`text-xs ${isDark ? 'text-white/50' : 'text-heliona-text-gray'}`} data-id="element-199">
                  {metric.sub}
                </div>
              </div>)}
          </motion.div>
        </div>
      </div>
    </motion.div>;
}